import { useEffect, useMemo, useState } from 'react';

import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import {
  functionalUpdate,
  PaginationState,
  Updater,
} from '@tanstack/react-table';
import { useSelector } from 'react-redux';

import type { RootState } from 'src/app/store/types';
import { DataGrid } from 'src/common/DataGrid';
import { useTagsListQuery } from 'src/common/external/bambi-api/bambiApi';
import { DEFAULT_PAGINATION_PAGE_SIZE } from 'src/common/external/bambi-api/constants';

import { TagEmptyState } from '../TagEmptyState';
import { TagForm } from '../TagForm/TagForm';
import { columns } from './columns';

export function TagManagementRoute() {
  const tagSearchTerm = useSelector(
    (state: RootState) => state.setting.tagSearchTerm
  );
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGINATION_PAGE_SIZE,
  });

  const {
    data: tags,
    isLoading,
    refetch,
  } = useTagsListQuery({
    page: pageIndex + 1,
    search: tagSearchTerm,
  });

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const handlePaginationChange = (
    paginationUpdater: Updater<PaginationState>
  ) => {
    setPagination((old: any) => functionalUpdate(paginationUpdater, old));
    refetch();
  };

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: DEFAULT_PAGINATION_PAGE_SIZE });
    refetch();
  }, [refetch, tagSearchTerm]);

  return (
    <>
      {isLoading ? (
        <div className="my-72 flex justify-center">
          <LoadingIndicator />
        </div>
      ) : (
        <>
          <div>
            <DataGrid
              tableId="tag-management"
              columns={columns}
              data={tags?.results || []}
              pagination={pagination}
              totalCount={tags?.count || 0}
              handlePaginationChange={handlePaginationChange}
              emptyState={<TagEmptyState />}
              dataTestId="tag-management-table"
              getRowId={(row) => row.id}
            />
          </div>
        </>
      )}
      <TagForm />
    </>
  );
}
